import {
  ApiError,
  BaseHttpRequest,
  CancelablePromise,
  PxtraClient,
  type OpenAPIConfig,
} from "./gen";
import * as jose from "jose";
import type { ApiRequestOptions } from "./gen/core/ApiRequestOptions";
import { request as __request } from "./gen/core/request";
import { Mutex } from "async-mutex";
import { writable } from "../core/helper";
import type { Storage } from "../core/storage";

interface TokenPair {
  access: string;
  refresh: string;
  accessExpiration?: Date;
}

type HttpRequestConstructor = new (config: OpenAPIConfig) => BaseHttpRequest;
type ApiErrorHandler = (error: ApiError) => boolean;

const PXTRA_ACCESS_TOKEN_KEY = "PXTRA_ACCESS_TOKEN";
const PXTRA_REFRESH_TOKEN_KEY = "PXTRA_REFRESH_TOKEN";
const PXTRA_ACCESS_EXPIRATION_KEY = "PXTRA_ACCESS_EXPIRATION";
const PXTRA_CURRENT_USER_KEY = "PXTRA_CURRENT_USER";

export class PxtraApiClientLegacy extends PxtraClient {
  private _refreshMutex = new Mutex();
  private _isLoggedIn = writable(false);
  private _ephemeralToken = writable<string | null | undefined>(undefined);
  private _twoFactorMethod = writable<string | null | undefined>(undefined);
  private _currentUser = writable<string | null | undefined>(undefined);
  private _storage: Storage;

  constructor({
    config,
    HttpRequest,
    storage,
  }: {
    config?: Partial<OpenAPIConfig>;
    HttpRequest?: HttpRequestConstructor;
    storage?: Storage;
  }) {
    if (!HttpRequest) {
      HttpRequest = PxtraHttpRequest;
    }
    if (!storage) {
      storage = {
        getItem: (key) =>
          new Promise((resolve) => resolve(localStorage.getItem(key))),
        setItem: (key, value) =>
          new Promise((resolve) => resolve(localStorage.setItem(key, value))),
        removeItem: (key) =>
          new Promise((resolve) => resolve(localStorage.removeItem(key))),
      };
    }

    if (config?.BASE) {
      config.BASE += "/api/v1";
    }

    super(config, HttpRequest);
    this._storage = storage;
    (this.request as PxtraHttpRequest).attachErrorCallback(
      this._onError.bind(this),
    );
    this._getIsLoggedIn().then((result) => this._isLoggedIn.set(result));
    this._storage
      .getItem(PXTRA_CURRENT_USER_KEY)
      .then((user) => this._currentUser.set(user));
    if (config) {
      this.request.config.TOKEN = async () =>
        (await this._getAccessToken()) ?? "";
    }
  }

  async loginFirstStep(
    username: string,
    password: string,
    audience: string,
    pushToken?: string,
  ) {
    await this._clearTokenPair();
    const { access, refresh, ephemeral_token, method } =
      await this.auth.authTokenCreate({
        audience,
        requestBody: {
          username,
          password,
          push_token: pushToken,
        },
      });

    await this._storage.setItem(PXTRA_CURRENT_USER_KEY, username);
    this._currentUser.set(username);
    if (access && refresh) {
      await this._setTokenPair({ access, refresh });
    }
    if (ephemeral_token && method) {
      this._ephemeralToken.set(ephemeral_token);
      this._twoFactorMethod.set(method);
      return { twoFactorNextStep: true };
    }
    return { twoFactorNextStep: false };
  }

  async loginSecondStep(code: string, audience: string, pushToken?: string) {
    let ephemeralToken;
    this._ephemeralToken.subscribe((token) => (ephemeralToken = token));
    if (!ephemeralToken) return;
    const tokenPair = await this.auth.authTokenCodeCreate({
      audience,
      requestBody: {
        ephemeral_token: ephemeralToken,
        code,
        push_token: pushToken,
      },
    });
    await this._setTokenPair(tokenPair);
  }

  public async loginWithIdToken(idToken: string, clientId: string) {
    this._clearTokenPair();
    const tokenPair = await this.auth.authTokenExchangeIdTokenCreate({
      requestBody: {
        id_token: idToken,
        client_id: clientId,
      },
    });

    await this._setTokenPair(tokenPair);
  }

  public async loginWithOAuthCode(
    audience: string,
    authorizationCode: string,
    clientId: string,
    redirectUri?: string,
    pushToken?: string,
  ) {
    this._clearTokenPair();

    const tokenPair = await this.auth.authTokenExchangeCodeCreate({
      requestBody: {
        audience: audience,
        authorization_code: authorizationCode,
        client_id: clientId,
        redirect_uri: redirectUri,
        push_token: pushToken,
      },
    });

    await this._storage.setItem(PXTRA_CURRENT_USER_KEY, tokenPair.username);

    this._currentUser.set(tokenPair.username);

    await this._setTokenPair(tokenPair);
  }

  public async logout() {
    const tp = await this._getTokenPair();
    const isLoggedIn = await this._getIsLoggedIn();
    if (isLoggedIn) {
      this.auth.authTokenInvalidateCreate({
        requestBody: { refresh: tp?.refresh ?? "" },
      });
    }

    this._clearTokenPair();
    this._isLoggedIn.set(false);
  }

  async enableTwoFactor(method: string) {
    this.auth.authActivateCreate({ method });
  }

  async enableTwoFactorConfirm(method: string, code: string) {
    this.auth.authActivateConfirmCreate({ method, requestBody: { code } });
  }

  async disableTwoFactor(method: string, code: string) {
    this.auth.authDeactivateCreate({ method, requestBody: { code } });
  }

  async requestTwoFactorCode(method: string) {
    this.auth.authCodeRequestCreate({ requestBody: { method } });
  }

  async userTwoFactorMethods() {
    this.auth.authMfaUserActiveMethodsList({});
  }

  public setBaseUrl(url: string) {
    this.request.config.BASE = url;
  }

  public get isLoggedIn() {
    return {
      subscribe: this._isLoggedIn.subscribe,
    };
  }

  public get currentUser() {
    return {
      subscribe: this._currentUser.subscribe,
    };
  }

  private async _getIsLoggedIn() {
    const tp = await this._getTokenPair();
    return !!tp;
  }

  private _clearTokenPair() {
    this._currentUser.set(undefined);

    return Promise.all([
      this._storage.removeItem(PXTRA_ACCESS_TOKEN_KEY),
      this._storage.removeItem(PXTRA_REFRESH_TOKEN_KEY),
      this._storage.removeItem(PXTRA_ACCESS_EXPIRATION_KEY),
      this._storage.removeItem(PXTRA_CURRENT_USER_KEY),
    ]);
  }

  private _getAccessTokenExpiration(accessToken: string) {
    if (!accessToken) {
      return undefined;
    }

    const decodedToken = jose.decodeJwt(accessToken);
    const expirationDate = decodedToken.exp
      ? new Date(decodedToken.exp * 1000)
      : undefined;

    // refresh tokens 30 seconds prior to expiration to avoid 401 responses which will trigger a logout
    if (expirationDate) {
      expirationDate.setSeconds(expirationDate.getSeconds() - 30);
    }

    return expirationDate;
  }

  private async _getAccessToken() {
    let tp = await this._getTokenPair();
    await this._handleTokenPairExpiration();
    tp = await this._getTokenPair();
    return tp?.access;
  }

  private async _handleTokenPairExpiration() {
    await this._refreshMutex.runExclusive(async () => {
      const tp = await this._getTokenPair();
      if (tp?.accessExpiration && new Date() >= tp?.accessExpiration) {
        console.log("Access token expired, refreshing...");
        await this._refreshTokenPair();
      }
    });
  }

  private async _refreshTokenPair() {
    const tp = await this._getTokenPair();

    if (!tp) return;
    // We need to make a manual request here, otherwise the Mutex will deadlock
    const result = await fetch(
      `${this.request.config.BASE}/auth/token/refresh/`,
      {
        method: "POST",
        body: JSON.stringify({ refresh: tp.refresh }),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const tokenPair = await result.json();

    await this._setTokenPair(tokenPair);
  }

  private async _setTokenPair(tokenPair: TokenPair) {
    const expirationDate = this._getAccessTokenExpiration(tokenPair.access);
    await Promise.all([
      this._storage.setItem(PXTRA_ACCESS_TOKEN_KEY, tokenPair.access),
      this._storage.setItem(PXTRA_REFRESH_TOKEN_KEY, tokenPair.refresh),
      this._storage.setItem(
        PXTRA_ACCESS_EXPIRATION_KEY,
        expirationDate?.toISOString() ?? "",
      ),
    ]);
    const isLoggedIn = await this._getIsLoggedIn();
    this._isLoggedIn.set(isLoggedIn);
  }

  private _onError(e: ApiError) {
    if (e.status === 401) {
      console.log("Got 401 response, clearing token pair");
      this._clearTokenPair();
      this._isLoggedIn.set(false);
      return true;
    }
    return false;
  }

  private async _getTokenPair() {
    const [accessToken, refreshToken, accessExpiration] = await Promise.all([
      this._storage.getItem(PXTRA_ACCESS_TOKEN_KEY),
      this._storage.getItem(PXTRA_REFRESH_TOKEN_KEY),
      this._storage.getItem(PXTRA_ACCESS_EXPIRATION_KEY),
    ]);

    if (!accessToken || !refreshToken || !accessExpiration) {
      this._isLoggedIn.set(false);
      return undefined;
    }

    const accessExpirationDate = new Date(accessExpiration);

    return {
      access: accessToken,
      refresh: refreshToken,
      accessExpiration: accessExpirationDate,
    };
  }
}

export class PxtraHttpRequest extends BaseHttpRequest {
  private _errorHandler?: ApiErrorHandler;

  constructor(config: OpenAPIConfig) {
    super(config);
  }

  public attachErrorCallback(callback: ApiErrorHandler) {
    this._errorHandler = callback;
  }

  public override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
    return new CancelablePromise(async (resolve, reject, onCancel) => {
      const promise = __request<T>(this.config, options);
      promise
        .then((r) => resolve(r))
        .catch((e) => {
          this._errorHandler?.(e as ApiError);
          reject(e);
        });
      if (onCancel.isCancelled) {
        promise.cancel();
      }
    });
  }
}
